<div class="dashboard-container">
  <div class="dashboard-topbar">
    <div class="topbar-left">
      <h1>
        System Dashboard
        <small>Same stock, rentals, and jobs as the screens below</small>
      </h1>
      <div class="breadcrumb">
        <i class="bi bi-house-door"></i> IMS / <span>Dashboard</span>
        <span class="separator">|</span>
        <span class="time"><i class="bi bi-clock"></i> {{ timestamp() }}</span>
      </div>
    </div>
    <div class="topbar-right">
      <button
        class="btn btn-outline btn-sm"
        type="button"
        (click)="refresh()"
        [disabled]="isLoading()"
        title="Refresh dashboard"
      >
        <i class="bi bi-arrow-clockwise" [class.spin]="isLoading()"></i>
        Refresh
      </button>
      <button class="btn btn-outline btn-sm" type="button" (click)="exportData()">
        <i class="bi bi-download"></i> Export
      </button>
    </div>
  </div>

  @if (isLoading()) {
    <div class="dash-loading" role="status" aria-live="polite">
      <div class="dash-spinner"></div>
      <span>Loading dashboard…</span>
    </div>
  }

  <div class="kpi-grid" [class.is-loading]="isLoading()">
    <div class="kpi-card alert" [routerLink]="['/check-in']" [queryParams]="{ status: 'overdue' }">
      <div class="kpi-top">
        <div>
          <div class="kpi-label">Overdue Returns</div>
          <div class="kpi-value">{{ overdueCount() }}</div>
        </div>
        <div class="kpi-icon red"><i class="bi bi-exclamation-circle"></i></div>
      </div>
      <div class="kpi-footer"><span class="badge danger">Check-In</span></div>
    </div>

    <div class="kpi-card alert" routerLink="/check-in">
      <div class="kpi-top">
        <div>
          <div class="kpi-label">Due Today</div>
          <div class="kpi-value">{{ equipmentStats().dueToday }}</div>
        </div>
        <div class="kpi-icon blue"><i class="bi bi-calendar-check"></i></div>
      </div>
      <div class="kpi-footer"><span class="sub-text">Returns due today</span></div>
    </div>

    <div class="kpi-card alert" routerLink="/low-stock">
      <div class="kpi-top">
        <div>
          <div class="kpi-label">Low Stock</div>
          <div class="kpi-value">{{ lowStockCount() }}</div>
        </div>
        <div class="kpi-icon amber"><i class="bi bi-exclamation-triangle"></i></div>
      </div>
      <div class="kpi-footer"><span class="badge warning">Current Stock</span></div>
    </div>

    <div class="kpi-card alert" routerLink="/current-stock">
      <div class="kpi-top">
        <div>
          <div class="kpi-label">Out of Stock</div>
          <div class="kpi-value">{{ outOfStockCount() }}</div>
        </div>
        <div class="kpi-icon red"><i class="bi bi-x-circle"></i></div>
      </div>
      <div class="kpi-footer"><span class="badge danger">Zero on hand</span></div>
    </div>

    <div class="kpi-card alert" routerLink="/damaged-lost">
      <div class="kpi-top">
        <div>
          <div class="kpi-label">Damaged / Lost</div>
          <div class="kpi-value">{{ damagedCount() }}</div>
        </div>
        <div class="kpi-icon red"><i class="bi bi-tools"></i></div>
      </div>
      <div class="kpi-footer"><span class="badge danger">Damaged / Lost</span></div>
    </div>

    <div class="kpi-card alert" routerLink="/maintenance-jobs">
      <div class="kpi-top">
        <div>
          <div class="kpi-label">Open Maintenance</div>
          <div class="kpi-value">{{ openMaintenanceCount() }}</div>
        </div>
        <div class="kpi-icon amber"><i class="bi bi-wrench-adjustable"></i></div>
      </div>
      <div class="kpi-footer"><span class="badge warning">Pending + in progress</span></div>
    </div>
  </div>

  <div class="summary-panels" [class.is-loading]="isLoading()">
    <section class="summary-panel">
      <div class="panel-header">
        <h3><i class="bi bi-box-seam"></i> Stock</h3>
        <a routerLink="/current-stock" class="panel-link">View stock <i class="bi bi-arrow-right"></i></a>
      </div>
      <div class="panel-metrics">
        <div class="metric" routerLink="/item-master">
          <span class="metric-value">{{ itemStats().activeItems }}</span>
          <span class="metric-label">Active SKUs</span>
        </div>
        <div class="metric" routerLink="/stock-valuation">
          <span class="metric-value">{{ formatCurrency(inventoryValue()) }}</span>
          <span class="metric-label">Inventory value</span>
        </div>
        <div class="metric" routerLink="/goods-received">
          <span class="metric-value">{{ goodsReceivedToday() }}</span>
          <span class="metric-label">Received today</span>
        </div>
        <div class="metric" routerLink="/issued-stock">
          <span class="metric-value">{{ issuesToday() }}</span>
          <span class="metric-label">Issued today</span>
        </div>
      </div>
    </section>

    <section class="summary-panel">
      <div class="panel-header">
        <h3><i class="bi bi-truck"></i> Equipment</h3>
        <a routerLink="/check-out" class="panel-link">Issue equipment <i class="bi bi-arrow-right"></i></a>
      </div>
      <div class="panel-metrics">
        <div class="metric" routerLink="/asset-register">
          <span class="metric-value">{{ equipmentStats().available }}</span>
          <span class="metric-label">Available</span>
        </div>
        <div class="metric" routerLink="/check-in">
          <span class="metric-value">{{ equipmentStats().checkedOut }}</span>
          <span class="metric-label">Checked out</span>
        </div>
        <div class="metric" routerLink="/reservations">
          <span class="metric-value">{{ reservedCount() }}</span>
          <span class="metric-label">Reserved</span>
        </div>
        <div class="metric" routerLink="/maintenance-jobs">
          <span class="metric-value">{{ inRepairCount() }}</span>
          <span class="metric-label">In repair</span>
        </div>
      </div>
    </section>
  </div>

  <div class="lists-grid">
    <section class="card">
      <div class="card-header">
        <h3><i class="bi bi-exclamation-circle"></i> Overdue returns</h3>
        <a routerLink="/check-in" class="panel-link">Check-In <i class="bi bi-arrow-right"></i></a>
      </div>
      <div class="card-body">
        <div class="activity-list">
          @for (row of overdueReturns(); track row.id) {
            <a class="activity-item" routerLink="/check-in">
              <div class="activity-content">
                <span class="activity-description">{{ checkoutLabel(row) }}</span>
                <span class="activity-time">Due {{ formatDate(row.dueDate) }}</span>
              </div>
            </a>
          } @empty {
            <div class="empty-state compact">
              <p>No overdue returns</p>
            </div>
          }
        </div>
      </div>
    </section>

    <section class="card">
      <div class="card-header">
        <h3><i class="bi bi-exclamation-triangle"></i> Low stock</h3>
        <a routerLink="/low-stock" class="panel-link">Low Stock <i class="bi bi-arrow-right"></i></a>
      </div>
      <div class="card-body">
        <div class="activity-list">
          @for (row of lowStockAlerts(); track row.id) {
            <a class="activity-item" routerLink="/current-stock">
              <div class="activity-content">
                <span class="activity-description">{{ snapshotLabel(row) }}</span>
                <span class="activity-time">{{ row.quantity }} on hand</span>
              </div>
            </a>
          } @empty {
            <div class="empty-state compact">
              <p>No low stock lines</p>
            </div>
          }
        </div>
      </div>
    </section>

    <section class="card">
      <div class="card-header">
        <h3><i class="bi bi-clock-history"></i> Recent activity</h3>
        <a routerLink="/issued-stock" class="panel-link">Issued Stock <i class="bi bi-arrow-right"></i></a>
      </div>
      <div class="card-body">
        <div class="activity-list">
          @for (row of recentFeed(); track row.id) {
            <a class="activity-item" [routerLink]="row.route">
              <div class="activity-content">
                <span class="activity-description">
                  {{ row.kind === 'issue' ? 'Issued' : 'Checked out' }} · {{ row.title }} · {{ row.detail }}
                </span>
                <span class="activity-time">{{ formatDate(row.date) }}</span>
              </div>
            </a>
          } @empty {
            <div class="empty-state compact">
              <p>No recent issues or checkouts</p>
            </div>
          }
        </div>
      </div>
    </section>
  </div>

  <div class="actions-section">
    <h3><i class="bi bi-lightning"></i> Quick Actions</h3>
    <div class="actions-grid">
      <button class="action-btn primary" routerLink="/goods-received">
        <i class="bi bi-box-arrow-in-down"></i>
        <span>Receive Stock</span>
      </button>
      <button class="action-btn success" routerLink="/issue-stock">
        <i class="bi bi-box-arrow-up"></i>
        <span>Issue Stock</span>
      </button>
      <button class="action-btn blue" routerLink="/check-out">
        <i class="bi bi-box-arrow-right"></i>
        <span>Equipment Checkout</span>
      </button>
      <button class="action-btn warning" routerLink="/check-in">
        <i class="bi bi-arrow-return-left"></i>
        <span>Equipment Return</span>
      </button>
      <button class="action-btn purple" routerLink="/reservations">
        <i class="bi bi-calendar-plus"></i>
        <span>Create Reservation</span>
      </button>
      <button class="action-btn info" routerLink="/reports">
        <i class="bi bi-bar-chart"></i>
        <span>View Reports</span>
      </button>
    </div>
  </div>
</div>

<div class="toast-container" [class.show]="showToast()">
  <div
    class="toast"
    [class.success]="toastType() === 'success'"
    [class.error]="toastType() === 'error'"
    [class.info]="toastType() === 'info'"
  >
    <div class="toast-icon">
      @if (toastType() === 'error') {
        <i class="bi bi-x-circle-fill"></i>
      } @else if (toastType() === 'info') {
        <i class="bi bi-info-circle-fill"></i>
      } @else {
        <i class="bi bi-check-circle-fill"></i>
      }
    </div>
    <div class="toast-content">
      <div class="toast-title">
        @if (toastType() === 'error') {
          Error
        } @else if (toastType() === 'info') {
          Info
        } @else {
          Success
        }
      </div>
      <div class="toast-message">{{ toastMessage() }}</div>
    </div>
    <button class="toast-close" type="button" (click)="closeToast()">
      <i class="bi bi-x"></i>
    </button>
  </div>
</div>
